import { Box, Flex, Heading, Image, useBreakpointValue } from "@chakra-ui/react"
import { BlockHeroEntityHydrated } from "@jackfruit/common"
import React from "react"
import Wrapper from "~/components/Wrapper"
import HeroButton from "../components/HeroButton"
import {
  getAllBackgroundImages,
  getAllButtons,
  getAllPartnerLogos,
  getContent,
  getTitle,
} from "../utils/helpers"

interface Props {
  config: BlockHeroEntityHydrated
}

const HeroBlockTemplate1: React.FC<Props> = ({ config }) => {
  const { title, content } = config

  const allBackgroundImages = getAllBackgroundImages(config)
  const allPartnerLogos = getAllPartnerLogos(config)
  const allButtons = getAllButtons(config)

  const hasPartnerLogos = allPartnerLogos.length > 0
  const hasButtons = allButtons.length > 0

  const backgroundImageMobile = allBackgroundImages.find(
    image => image.type === "mobile"
  )
  const backgroundImageTablet = allBackgroundImages.find(
    image => image.type === "tablet"
  )
  const backgroundImageDesktop = allBackgroundImages.find(
    image => image.type === "desktop"
  )

  const mobileImage = useBreakpointValue({
    base: backgroundImageMobile,
    md: backgroundImageTablet,
    lg: backgroundImageDesktop,
  })

  return (
    <Wrapper
      py={{ base: 6, lg: 8 }}
      pt={{ base: 0, lg: undefined }}
      className="hero-block-template1"
    >
      <Flex
        minHeight={{ base: undefined, lg: "460px" }}
        borderRadius="xl"
        backgroundColor={{ base: undefined, lg: "blackAlpha.100" }}
        backgroundImage={{
          base: "none",
          lg: backgroundImageDesktop?.path,
        }}
        backgroundPosition={{ base: "center" }}
        backgroundRepeat="no-repeat"
        backgroundSize="cover"
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          flex={1}
          gap={5}
          alignItems={{ base: "center", lg: "flex-start" }}
          px={{ base: 8, lg: 16 }}
          py={10}
          maxWidth={{ base: undefined, lg: "50%" }}
        >
          <Heading
            as="h1"
            size="xl"
            color="gray.800"
            textAlign={{ base: "center", lg: "left" }}
          >
            {getTitle(title)}
          </Heading>
          {hasPartnerLogos && (
            <Flex
              flexDirection="row"
              gap={4}
              flexWrap="wrap"
              justifyContent={{ base: "center", lg: "flex-start" }}
            >
              {allPartnerLogos.map((partnerLogo, index) => {
                return (
                  <Image
                    key={index}
                    src={partnerLogo.path}
                    alt={partnerLogo.alt}
                    height="40px"
                    width="auto"
                    objectFit="contain"
                  />
                )
              })}
            </Flex>
          )}
          <Box
            className="hero-content"
            dangerouslySetInnerHTML={{ __html: getContent(content) }}
            textAlign={{ base: "center", lg: "left" }}
            maxWidth={{ base: "500px", lg: "full" }}
            mt={-2}
          />
          {hasButtons && (
            <Flex gap={4} flexDirection={{ base: "column", sm: "row" }}>
              {allButtons.map((button, index) => {
                return (
                  <HeroButton
                    key={index}
                    label={button.label}
                    action={button.action}
                    customAction={button.customAction}
                    isPrimary={button.isPrimary}
                  />
                )
              })}
            </Flex>
          )}
        </Flex>
        <Flex flex={{ base: 0, lg: 1 }} />
      </Flex>
      <Image
        backgroundColor={{ base: "blackAlpha.100", lg: undefined }}
        src={mobileImage?.path}
        alt={mobileImage?.alt || title}
        display={{ base: "flex", lg: "none" }}
        borderRadius="lg"
        objectFit="cover"
        width="100%"
        height="auto"
        htmlHeight="400px"
        htmlWidth="740px"
      />
    </Wrapper>
  )
}

export default HeroBlockTemplate1
